import { History, Loader2, Search, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { buildLoadDetailBackState, LOADS_LIST_PATH } from '@/hooks/useOpenLoadDetail'
import { Kbd } from 'components/ds2/Kbd'
import { MIN_QUERY_LENGTH, useGlobalSearch } from '../hooks/useGlobalSearch'
import { SearchCategory, SearchResultItem } from '../types/types'
import { getScopeMeta, ScopePicker } from './ScopePicker'
import { SearchResultRow } from './SearchResultItem'

const CATEGORY_ORDER: SearchCategory[] = [
    SearchCategory.Loads,
    SearchCategory.Invoices,
    SearchCategory.Trucks,
    SearchCategory.Drivers,
    SearchCategory.Contractors,
    SearchCategory.Payroll,
    SearchCategory.Brokers,
    SearchCategory.Trips,
]
const CATEGORY_LABELS: Record<SearchCategory, string> = {
    [SearchCategory.Loads]: 'Loads',
    [SearchCategory.Invoices]: 'Invoices',
    [SearchCategory.Trucks]: 'Trucks',
    [SearchCategory.Drivers]: 'Drivers',
    [SearchCategory.Contractors]: 'Contractors',
    [SearchCategory.Payroll]: 'Payroll',
    [SearchCategory.Brokers]: 'Brokers',
    [SearchCategory.Trips]: 'Trips',
}

interface GlobalSearchBarProps {
    autoFocus?: boolean
    /** Called right after a result is selected and navigation fires — lets the caller (the sidebar's
     * search overlay) dismiss itself instead of sitting on top of the page it just navigated to. */
    onNavigate?: () => void
}

export function GlobalSearchBar({ autoFocus = false, onNavigate }: GlobalSearchBarProps) {
    const navigate = useNavigate()
    const location = useLocation()
    const wrapperRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLDivElement>(null)
    const [open, setOpen] = useState(autoFocus)
    const {
        query,
        setQuery,
        scope,
        setScope,
        results,
        loading,
        error,
        activeIndex,
        setActiveIndex,
        recents,
        saveRecentSearch,
        removeRecentSearch,
        clearRecentSearches,
    } = useGlobalSearch()

    // Focus on mount when opened via the sidebar trigger/⌘K — the native `autoFocus` attribute is
    // unreliable on an element mounted fresh inside a portal (the modal only mounts this component
    // once `modalOpen` flips true in `SidebarSearchButton`), so focus explicitly instead. Snapshotting
    // the initial value in a ref (rather than listing `autoFocus` as a dependency) keeps this
    // deliberately mount-once — a later prop change shouldn't yank focus back into the input.
    const initialAutoFocusRef = useRef(autoFocus)
    useEffect(() => {
        if (initialAutoFocusRef.current) inputRef.current?.focus()
    }, [])

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault()
                inputRef.current?.focus()
                setOpen(true)
            }
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [])

    useEffect(() => {
        if (!open) return
        const handler = (e: MouseEvent) => {
            const target = e.target as Element
            // The scope menu is portaled outside the wrapper — a click on it isn't an "outside" click,
            // so don't close the results dropdown (the pick re-runs the query and results stay visible).
            if (target.closest?.('[data-scope-portal]')) return
            if (wrapperRef.current && !wrapperRef.current.contains(target)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open])

    const handleSelect = useCallback(
        (item: SearchResultItem) => {
            saveRecentSearch(query)
            setOpen(false)
            inputRef.current?.blur()
            // A load detail page sends Back to wherever the search was made from, like every other way into it.
            const backState = item.path.startsWith(`${LOADS_LIST_PATH}/`)
                ? buildLoadDetailBackState(item.path, location, item.state?.loadId as string | undefined)
                : {}
            navigate(item.path, { state: { ...item.state, ...backState, globalSearchHighlightId: item.id } })
            onNavigate?.()
        },
        [query, saveRecentSearch, navigate, location, onNavigate]
    )

    const handleRecentClick = useCallback((text: string) => setQuery(text), [setQuery])

    // Compute groupedItems once; derive renderOrderedItems and indexMap from it — O(n).
    const groupedItems = useMemo(() => {
        const map = new Map<SearchCategory, SearchResultItem[]>()
        for (const r of results) {
            const arr = map.get(r.category) ?? []
            arr.push(r)
            map.set(r.category, arr)
        }
        return CATEGORY_ORDER.map((cat) => ({ category: cat, items: map.get(cat) ?? [] })).filter((g) => g.items.length > 0)
    }, [results])

    const renderOrderedItems = useMemo<SearchResultItem[]>(() => groupedItems.flatMap((g) => g.items), [groupedItems])

    const indexMap = useMemo(() => {
        const map = new Map<string, number>()
        let i = 0
        for (const g of groupedItems) for (const item of g.items) map.set(item.id + '::' + item.category, i++)
        return map
    }, [groupedItems])

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'ArrowDown' && renderOrderedItems.length > 0) {
                e.preventDefault()
                setActiveIndex((prev) => Math.min(prev + 1, renderOrderedItems.length - 1))
            } else if (e.key === 'ArrowUp' && renderOrderedItems.length > 0) {
                e.preventDefault()
                setActiveIndex((prev) => Math.max(prev - 1, 0))
            } else if (e.key === 'Enter' && renderOrderedItems.length > 0) {
                e.preventDefault()
                const item = renderOrderedItems[activeIndex]
                if (item) handleSelect(item)
            } else if (e.key === 'Escape') {
                e.preventDefault()
                setOpen(false)
                inputRef.current?.blur()
            }
        },
        [renderOrderedItems, activeIndex, setActiveIndex, handleSelect]
    )

    useEffect(() => {
        if (!open || !listRef.current) return
        const active = listRef.current.querySelector('[aria-selected="true"]')
        if (active) active.scrollIntoView({ block: 'nearest' })
    }, [activeIndex, open])

    const hasResults = results.length > 0
    const isSearching = query.length >= MIN_QUERY_LENGTH && loading
    const showRecent = !query && recents.length > 0
    // Only show "no results" once a search has actually settled (not loading) — never during the debounce gap.
    const showEmpty = query.length >= MIN_QUERY_LENGTH && !loading && !hasResults && !error
    // While the first search for a query is in flight (no prior results to keep showing), render a
    // stable "Searching…" row instead of an empty box, so the dropdown content doesn't flash.
    const showLoading = isSearching && !hasResults
    const showDropdown = open && (hasResults || showRecent || showEmpty || error || showLoading)

    const scopePlaceholder = scope === 'all' ? 'Search loads, trucks, drivers, brokers...' : `Search ${getScopeMeta(scope).label.toLowerCase()}...`

    return (
        <div ref={wrapperRef} className="w-full overflow-hidden rounded-3xl border border-border bg-surface shadow-overlay">
            {/* ── Input row ─────────────────────────────────────────── */}
            <div className="flex items-center gap-3 px-4 py-3.5">
                <Search size={20} className="shrink-0 text-foreground-muted" />
                <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={scopePlaceholder}
                    autoComplete="off"
                    className="min-w-0 flex-1 bg-transparent text-[15px] text-foreground outline-none placeholder:text-foreground-muted"
                />
                {loading && <Loader2 size={16} className="shrink-0 animate-spin text-foreground-muted" />}
                {query && !loading && (
                    <button
                        type="button"
                        onClick={() => setQuery('')}
                        className="shrink-0 rounded-lg p-1 text-foreground-muted transition-colors hover:bg-default"
                    >
                        <X size={16} />
                    </button>
                )}
                <ScopePicker scope={scope} onChange={setScope} />
                <span className="h-5 w-px shrink-0 bg-border" />
                <Kbd shortcut="key" keyLabel="ESC" />
            </div>

            {showDropdown && (
                <>
                    <div className="border-t border-border" />
                    <div ref={listRef} className="max-h-[60vh] overflow-y-auto p-2" role="listbox">
                        {showRecent && (
                            <div className="p-1">
                                <div className="flex items-center justify-between px-2 py-1.5">
                                    <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">Recent</span>
                                    <button
                                        type="button"
                                        onClick={clearRecentSearches}
                                        className="text-xs text-foreground-muted transition-colors hover:text-foreground"
                                    >
                                        Clear all
                                    </button>
                                </div>
                                {recents.map((text) => (
                                    <div
                                        key={text}
                                        onClick={() => handleRecentClick(text)}
                                        className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-2.5 transition-colors hover:bg-default"
                                    >
                                        <History size={16} className="text-foreground-muted" />
                                        <span className="flex-1 text-sm text-foreground">{text}</span>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                removeRecentSearch(text)
                                            }}
                                            className="rounded-lg p-1 text-foreground-muted transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {hasResults && (
                            <div className="p-1">
                                {groupedItems.map((group) => (
                                    <div key={group.category} className="mb-2 last:mb-0">
                                        <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
                                            {CATEGORY_LABELS[group.category]}
                                        </div>
                                        {group.items.map((item) => {
                                            const idx = indexMap.get(item.id + '::' + item.category) ?? 0
                                            return (
                                                <SearchResultRow
                                                    key={item.id + '::' + item.category}
                                                    item={item}
                                                    isActive={idx === activeIndex}
                                                    query={query}
                                                    onClick={() => handleSelect(item)}
                                                    onMouseEnter={() => setActiveIndex(idx)}
                                                />
                                            )
                                        })}
                                    </div>
                                ))}
                            </div>
                        )}

                        {showLoading && (
                            <div className="flex items-center justify-center gap-2 py-8">
                                <Loader2 size={16} className="animate-spin text-foreground-muted" />
                                <span className="text-sm text-foreground-muted">Searching…</span>
                            </div>
                        )}

                        {error && !loading && <div className="mx-2 my-2 rounded-xl bg-warning-soft px-3 py-2 text-sm text-foreground">{error}</div>}

                        {showEmpty && !error && (
                            <div className="flex flex-col items-center justify-center px-4 py-10">
                                <Search size={32} className="mb-2 text-foreground-muted" />
                                <span className="text-sm text-foreground-muted">No results found for &ldquo;{query}&rdquo;</span>
                                <span className="mt-1 text-xs text-foreground-muted">Try searching by load ID, driver name, or MC number</span>
                            </div>
                        )}
                    </div>

                    {hasResults && (
                        <div className="flex items-center gap-4 border-t border-border px-4 py-2.5">
                            <div className="flex items-center gap-1.5">
                                <Kbd shortcut="key" keyLabel="↑↓" />
                                <span className="text-xs text-foreground-muted">navigate</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Kbd shortcut="key" keyLabel="↵" />
                                <span className="text-xs text-foreground-muted">open</span>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
